import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { ShieldCheck, RefreshCw } from "lucide-react";

const ACTION_COLORS = {
  create: "bg-emerald-50 text-emerald-700",
  update: "bg-blue-50 text-blue-700",
  delete: "bg-red-50 text-red-700",
  admin_login_success: "bg-emerald-50 text-emerald-700",
  admin_login_failed: "bg-red-50 text-red-700",
  admin_otp_failed: "bg-amber-50 text-amber-700",
  admin_logout: "bg-slate-100 text-slate-600",
};

function actionColor(action) {
  if (ACTION_COLORS[action]) return ACTION_COLORS[action];
  const prefix = (action || "").split(":")[0];
  return ACTION_COLORS[prefix] || "bg-slate-100 text-slate-600";
}

export default function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit-logs", { params: { limit: 200, action: filter || undefined } });
      setLogs(data.logs);
      setTotal(data.total);
    } catch {
      /* handled by interceptor */
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-blue-600" /> Audit Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">Every admin action is recorded — {total} entries</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by action (e.g. delete, login)"
            data-testid="audit-filter-input"
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-blue-500 outline-none w-64"
          />
          <button
            onClick={load}
            data-testid="audit-refresh-btn"
            className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-log-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Endpoint</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {new Date(l.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3 text-slate-900 font-medium">{l.actor_email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${actionColor(l.action)}`}>
                      {l.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{l.method} {l.path}</td>
                  <td className="px-4 py-3 text-slate-600">{l.status_code ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{l.ip || "—"}</td>
                </tr>
              ))}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400">No audit entries yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
